// checkout/steps/deliveryAddressListeners.ts

import { checkoutData } from '../init';
import { submitFormAndUpdateStep } from '../utils/submitForm';
import { populateForm } from '../utils/populateForm';
import { registerStepFocusCallback } from '../utils/stepFocusCallbacks';
import { toggleClass } from '../../utils/toggleClass';
import { toggleState } from '../../utils/toggleState';
import { initDeliveryLoqateLookup } from '../utils/initDeliveryLoqateLookup';

type CountryOption = {
	code: string;
	name: string;
	isAllowedForDelivery: string; // 'true' | 'false'
};

type DeliveryInstructionOption = {
	id: string;
	description: string;
};

/**
 * Initialise the Delivery Address step.
 * - Populates selects and inputs from `checkoutData`.
 * - Wires submit to POST and advance the step.
 * - Auto-reveals delivery notes if pre-filled.
 * - Registers a focus callback to initialise Loqate (PCA) on demand.
 *
 * @returns {void}
 */
export function addDeliveryAddressListeners(): void {
	const formId = 'deliveryAddressForm';
	const form = document.getElementById(formId) as HTMLFormElement | null;
	if (!form) return;

	// Populate static + dynamic fields when data provided by backend.
	const stepData = checkoutData.deliveryAddress;
	const isDigitalOnly = stepData?.isDigitalOnly === 'true';

	if (stepData?.fields) {
		const { fields, titles, countries, deliveryInstructions } = stepData;
		const dynamicOptions = {
			'deliveryAddress.address.title': titles ?? [],
			'deliveryAddress.address.countryCode': (countries as CountryOption[])
				// For digital show all countries. Physical delivery, only show allowed countries.
				.filter((c) => isDigitalOnly || c.isAllowedForDelivery === 'true')
				.map((c) => ({ value: c.code, text: c.name })),
			'deliveryAddress.deliveryInstructionId': (
				deliveryInstructions as DeliveryInstructionOption[]
			).map((inst) => ({ value: inst.id, text: inst.description })),
		};

		populateForm(formId, fields, dynamicOptions);
	} else {
		console.warn(`⚠️ '${formId}' fields unavailable; skipping form population.`);
	}

	/**
	 * Submit handler. Posts form and updates the step container.
	 */
	form.addEventListener('submit', async (e) => {
		e.preventDefault();
		const submitBtn = form.querySelector('button[type="submit"]') as HTMLElement;
		if (submitBtn) toggleClass([submitBtn], ['btn-loading']);
		const action = checkoutData.deliveryAddress?.action;
		await submitFormAndUpdateStep(form, action);
	});

	/**
	 * If delivery notes were already set, reveal the notes area.
	 */
	(() => {
		const notesInput = form.querySelector<HTMLInputElement>('#delivery-instructions-textarea');
		if (!notesInput || !notesInput.value) return;

		const notesRow = form.querySelector<HTMLElement>('.form__row--delivery-notes');
		const notesToggle = form.querySelector<HTMLElement>(
			'[data-form-target="form__row--delivery-notes"]',
		);

		if (notesRow) toggleClass([notesRow], ['form__row--hidden']);
		if (notesToggle) toggleState([notesToggle], 'hidden');
	})();

	/**
	 * Initialise Delivery PCA when the Delivery step is focused.
	 * Safe to call multiple times; the PCA utility tears down prior instances.
	 */
	registerStepFocusCallback('deliveryAddress', () => {
		const enabled = window.serverData?.pcaEnabled === 'true';
		const isDigitalOnly = checkoutData.deliveryAddress?.isDigitalOnly === 'true';
		const key = window.serverData?.pcaKey;
		if (enabled && key)
			void initDeliveryLoqateLookup({ limit: 10, isDelivery: !isDigitalOnly });
	});
}
